import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard } from './core/guards/auth.guard';
import { SubscriptionGuard } from './core/guards/subscription.guard';
import { ComingSoonGuard } from './core/guards/coming-soon.guard';
import { ConsentGuard } from './core/guards/consent.guard';
import { RoleGuard } from './core/guards/role.guard';

const routes: Routes = [
  // Coming Soon page (accessible when feature is enabled)
  { path: 'coming-soon', loadChildren: () => import('./pages/coming-soon/coming-soon.module').then(m => m.ComingSoonModule) },

  // Welcome/Consent screen (shown once before login on native platforms)
  { path: 'welcome', loadChildren: () => import('./pages/welcome-consent/welcome-consent.module').then(m => m.WelcomeConsentModule) },
  
  // Public routes (protected by coming-soon + consent guards)
  { path: 'login', loadChildren: () => import('./pages/login/login.module').then(m => m.LoginModule), canActivate: [ComingSoonGuard, ConsentGuard] },
  { path: 'registration', loadChildren: () => import('./pages/registration/registration.module').then(m => m.RegistrationModule), canActivate: [ComingSoonGuard, ConsentGuard] },
  { path: 'confirm-email', loadChildren: () => import('./pages/confirm-email/confirm-email.module').then(m => m.ConfirmEmailModule), canActivate: [ComingSoonGuard] },
  { path: 'forgot-password', loadChildren: () => import('./pages/forgot-password/forgot-password.module').then(m => m.ForgotPasswordModule), canActivate: [ComingSoonGuard] },
  {
    path: 'confirm-reset-password',
    loadChildren: () => import('./pages/confirm-reset-password/confirm-reset-password.module').then(m => m.ConfirmResetPasswordModule),
    canActivate: [ComingSoonGuard]
  },
  { path: 'verify-contact', loadChildren: () => import('./pages/verify-contact/verify-contact.module').then(m => m.VerifyContactModule), canActivate: [ComingSoonGuard] },
  
  // OAuth callback route - processes OAuth callback and completes authentication
  // This is critical for mobile OAuth flows to work properly
  { 
    path: 'auth/callback', 
    loadChildren: () => import('./pages/auth-callback/auth-callback.module').then(m => m.AuthCallbackModule),
    canActivate: [ComingSoonGuard]
  },
  
  // Protected routes (coming-soon + auth + subscription check)
  {
    path: 'home',
    loadChildren: () => import('./pages/home/home.module').then(m => m.HomeModule),
    canActivate: [ComingSoonGuard, AuthGuard, SubscriptionGuard]
  },
  // Billing & plans pages (coming-soon + auth required, but no subscription check)
  {
    path: 'billing',
    loadComponent: () => import('./pages/billing/billing.component').then(m => m.BillingComponent),
    canActivate: [ComingSoonGuard, AuthGuard]
  },
  {
    path: 'plans',
    loadComponent: () => import('./pages/billing/components/plan-selection/plan-selection.component').then(m => m.PlanSelectionComponent),
    canActivate: [ComingSoonGuard, AuthGuard]
  },
  {
    path: 'checkout-success',
    loadComponent: () => import('./pages/checkout-success/checkout-success.component').then(m => m.CheckoutSuccessComponent),
    canActivate: [ComingSoonGuard, AuthGuard]
  },
  {
    path: 'checkout-cancel',
    loadComponent: () => import('./pages/checkout-cancel/checkout-cancel.component').then(m => m.CheckoutCancelComponent),
    canActivate: [ComingSoonGuard, AuthGuard]
  },
  {
    path: 'avatars',
    loadChildren: () => import('./pages/avatars/avatars.module').then(m => m.AvatarsModule),
    canActivate: [ComingSoonGuard, AuthGuard, SubscriptionGuard]
  },
  {
    path: 'settings',
    loadChildren: () => import('./pages/settings/settings.module').then(m => m.SettingsModule),
    canActivate: [ComingSoonGuard, AuthGuard, SubscriptionGuard]
  },
  {
    path: 'voice-enrollment',
    loadChildren: () => import('./pages/voice-enrollment/voice-enrollment.module').then(m => m.VoiceEnrollmentModule),
    canActivate: [ComingSoonGuard, AuthGuard, SubscriptionGuard]
  },
  {
    path: 'matches',
    loadChildren: () => import('./pages/matches/matches.module').then(m => m.MatchesModule),
    canActivate: [ComingSoonGuard, AuthGuard, SubscriptionGuard]
  },
  
  // Manager routes (requires manager or admin role)
  {
    path: 'manager/feedback',
    loadChildren: () => import('./pages/manager/feedback-dashboard/feedback-dashboard.routes').then(m => m.FEEDBACK_DASHBOARD_ROUTES),
    canActivate: [ComingSoonGuard, AuthGuard, RoleGuard],
    data: { roles: ['manager', 'admin'] }
  },
  
  // Default redirects
  { path: '', redirectTo: 'home', pathMatch: 'full' },
  { path: '**', redirectTo: 'login' }
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule { }